import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import util from "../../utils/miniUtils";
import { apiErrorMessage } from "../../components/auth/apiError";

dayjs.extend( utc );
dayjs.extend( timezone );

// inventory-api guarda las fechas en UTC; el negocio las ve en hora de Tijuana (igual que el filtro de fechas de la API)
const BUSINESS_TIMEZONE = "America/Tijuana";

export const SHIPPING_STATUS = {
    TO_SHIP: "to_ship",
    SHIPPED: "shipped",
    DELIVERED: "delivered",
};

export const SHIPPING_LABELS = {
    [SHIPPING_STATUS.TO_SHIP]: "Por enviar",
    [SHIPPING_STATUS.SHIPPED]: "Enviado",
    [SHIPPING_STATUS.DELIVERED]: "Entregado",
};

// Estados de MercadoPago para el filtro; en la tabla se muestran tal cual llegan (§8.1)
export const PAYMENT_STATUS_FILTERS = [
    "pending", "in_process", "approved", "authorized", "rejected", "cancelled", "refunded", "in_mediation", "charged_back",
];
export const PAYMENT_STATUS_NONE = "none";

// Texto para el personal de cada status de MercadoPago (§10.1). Un valor que no esté aquí se muestra tal cual llega.
export const PAYMENT_STATUS_LABELS = {
    pending: "Pendiente",
    in_process: "En revisión",
    approved: "Aprobado",
    rejected: "Rechazado",
    cancelled: "Cancelado",
    refunded: "Reembolsado",
    in_mediation: "En disputa",
    charged_back: "Contracargo",
};

// status_detail. Tres detalles se repiten en distintos status, por eso esas llaves llevan el status: "status:detail".
export const PAYMENT_DETAIL_LABELS = {
    accredited: "Acreditado",
    partially_refunded: "Reembolso parcial",
    pending_challenge: "Verificación 3D Secure en curso",
    pending_contingency: "En validación",
    pending_review_manual: "Revisión manual de MercadoPago (hasta 2 días hábiles)",
    offline_process: "Procesando",
    deferred_retry: "Procesando",
    expired: "Venció sin pagarse",
    by_payer: "Cancelado por el cliente",
    by_collector: "Cancelado por la tienda",
    by_admin: "Reembolsado por la tienda",
    "refunded:refunded": "Reembolso total",
    "in_mediation:pending": "Disputa abierta por el cliente",
    settled: "Contracargo liquidado",
    reimbursed: "Contracargo reembolsado",
    "charged_back:in_process": "Contracargo en proceso",
    cc_rejected_insufficient_amount: "Fondos insuficientes",
    cc_rejected_bad_filled_card_number: "Número de tarjeta incorrecto",
    cc_rejected_bad_filled_date: "Fecha de vencimiento incorrecta",
    cc_rejected_bad_filled_security_code: "Código de seguridad incorrecto",
    cc_rejected_bad_filled_other: "Datos de la tarjeta incorrectos",
    cc_rejected_call_for_authorize: "El banco requiere autorización",
    cc_rejected_card_disabled: "Tarjeta inactiva",
    cc_rejected_high_risk: "Rechazado por seguridad",
    cc_rejected_max_attempts: "Máximo de intentos alcanzado",
    cc_rejected_duplicated_payment: "Pago duplicado",
    cc_rejected_other_reason: "Rechazado por el banco",
};

// Status del que MercadoPago ya no lo mueve: no tiene caso consultarlo de nuevo
const FINAL_PAYMENT_STATUSES = [ "rejected", "cancelled", "refunded" ];

// Color del badge (Bootstrap) de cada status; el que no esté aquí se ve neutro
const PAYMENT_STATUS_BADGES = {
    pending: "bg-secondary",
    in_process: "bg-warning text-dark",
    approved: "bg-success",
    rejected: "bg-danger",
    cancelled: "bg-secondary",
    refunded: "bg-info text-dark",
    in_mediation: "bg-warning text-dark",
    charged_back: "bg-danger",
};
const SHIPPING_BADGES = {
    [SHIPPING_STATUS.TO_SHIP]: "bg-warning text-dark",
    [SHIPPING_STATUS.SHIPPED]: "bg-primary",
    [SHIPPING_STATUS.DELIVERED]: "bg-success",
};
const NEUTRAL_BADGE = "bg-light text-dark border";

export const paymentBadgeClass = ( status ) => `badge ${PAYMENT_STATUS_BADGES[status] || NEUTRAL_BADGE}`;
export const shippingBadgeClass = ( status ) => `badge ${SHIPPING_BADGES[status] || NEUTRAL_BADGE}`;

export const paymentStatusLabel = ( status ) => PAYMENT_STATUS_LABELS[status] || status;

export const paymentDetailLabel = ( status, detail ) => PAYMENT_DETAIL_LABELS[`${status}:${detail}`] || PAYMENT_DETAIL_LABELS[detail] || detail;

export const isPaymentFinal = ( status ) => FINAL_PAYMENT_STATUSES.includes( status );

// Solo http(s): internal_id es texto libre y un "javascript:" no debe volverse link
export const isWebLink = ( value ) => typeof value === "string" && /^https?:\/\//i.test( value.trim() );

// order_alerts.reason de inventory-api (§8.3)
export const ALERT_LABELS = {
    paid_without_stock: "Pagado, pero la pieza ya no estaba disponible",
    approved_after_release: "Pago aprobado de un pedido ya liberado",
    amount_mismatch: "El monto del pago no coincide con el pedido",
    payment_in_mediation: "Pago en disputa",
    payment_charged_back: "Contracargo",
    unexpected_payment_status: "Estado de pago inesperado",
    duplicate_payment: "Pago duplicado",
    cancelled_by_customer: "El cliente canceló el pedido: falta reembolsar en MercadoPago",
    cancelled_by_staff: "Pedido cancelado: falta reembolsar en MercadoPago",
};

// orders.cancelled_by de inventory-api
export const CANCELLED_BY_LABELS = {
    customer: "el cliente",
    staff: "el personal",
};

// Tipos de archivo de la factura, tal cual los espera inventory-api
export const INVOICE_FILE_TYPE = {
    PDF: "pdf",
    XML: "xml",
};

// Guarda en el navegador un archivo que llegó como blob
export const saveBlobAsFile = ( blob, fileName ) => {
    const url = URL.createObjectURL( blob );
    const link = document.createElement( "a" );
    link.href = url;
    link.download = fileName;
    document.body.appendChild( link );
    link.click();
    link.remove();
    URL.revokeObjectURL( url );
};

export const formatMoney = ( value ) => Number( value ).toLocaleString( "es-MX", { style: "currency", currency: "MXN" } );

export const formatDateTime = ( date ) => {
    if ( !date ) return "—";
    return dayjs( date ).tz( BUSINESS_TIMEZONE ).format( "DD/MM/YYYY HH:mm" );
};

// Detalle del pedido: "DD/MM/YYYY, HH:mm hrs" y montos con la moneda explícita
export const formatDateTimeHrs = ( date ) => {
    if ( !date ) return "—";
    return `${dayjs( date ).tz( BUSINESS_TIMEZONE ).format( "DD/MM/YYYY, HH:mm" )} hrs`;
};

export const formatMxn = ( value ) => `${formatMoney( value )} MXN`;

// order_history.event de inventory-api
export const ORDER_EVENT_NOTE = "note";
export const EVENT_LABELS = {
    created: "Creado",
    released: "Liberado",
    payment: "Pago",
    shipping: "Envío",
    tracking: "Guía",
    address: "Dirección",
    alert: "Alerta",
    return: "Devolución",
    note: "Nota",
    invoice: "Factura",
    cancel: "Cancelación",
};

// util.formatPhone deja los teléfonos de 10 dígitos tal cual; aquí salen como (XXX) XXX-XXXX y el resto con el formato general
export const formatPhoneMx = ( phone ) => {
    const digits = String( phone ).replace( /\D/g, "" );
    if ( digits.length !== 10 ) return util.formatPhone( String( phone ) ) || String( phone );
    return `(${digits.slice( 0, 3 )}) ${digits.slice( 3, 6 )}-${digits.slice( 6 )}`;
};

// Actividad de la cuenta de vendedor (México). El detalle de una operación es /activities/detail/checkout_merchant_order-<hash>
// y ese hash no se puede armar con el ID del pago, así que se abre la lista y el ID se busca copiándolo
export const MERCADOPAGO_PANEL_URL = "https://www.mercadopago.com.mx/activities";

// Une solo las partes con valor: un campo opcional vacío no deja comas ni etiquetas sueltas
const joinFilled = ( parts, separator ) => parts.filter( ( part ) => part ).join( separator );

// Líneas de la dirección (calle, colonia y CP, ciudad); las que queden vacías no se incluyen
export const addressLines = ( address ) => {
    const number = joinFilled( [ address.exteriorNumber, address.interiorNumber ? `int. ${address.interiorNumber}` : "" ], " " );
    const zipCode = address.zipCode ? `CP ${address.zipCode}` : "";
    const lines = [
        joinFilled( [ address.street, number ], " " ),
        joinFilled( [ address.neighborhood, zipCode ], ", " ),
        joinFilled( [ address.municipality, address.city, address.state ], ", " ),
    ];
    return lines.filter( ( line ) => line );
};

// Texto de la dirección para pegarlo en la paquetería
export const addressToText = ( address ) => {
    const lines = [
        address.recipientName,
        address.recipientPhone ? formatPhoneMx( address.recipientPhone ) : "",
        ...addressLines( address ),
        address.betweenStreets ? `Entre: ${address.betweenStreets}` : "",
        address.addressReferences ? `Ref.: ${address.addressReferences}` : "",
    ];
    return lines.filter( ( line ) => line ).join( "\n" );
};

export const todayInBusinessTimezone = () => dayjs().tz( BUSINESS_TIMEZONE ).format( "YYYY-MM-DD" );

// 401 cierra la sesión (util.handleError); el resto muestra el msg de la API (400 de validación, 409 de regla de negocio)
export const showApiError = ( e ) => {
    const status = e && e.response ? e.response.status : undefined;
    if ( status === 401 ) return util.handleError( e );
    util.handleError( apiErrorMessage( e ) );
};
